// src/features/owner/sections/DashboardSection.tsx — the concept dashboard, wired to real data.
import styled from 'styled-components';
import { useOwnerDashboard } from '@/hooks';
import { useOwnerAction } from '@/hooks/useOwnerAction';
import { Avatar } from '@/ui/Avatar';
import { Button } from '@/ui';
import { Icon } from '@/icons';
import { KpiCard, OwnerPanel, EmptyState, StatusDot } from '../components';
import { ComboChart, Donut } from '../charts';

const KPIS = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
  margin-bottom: 14px;
  @media (max-width: 1200px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 640px) { grid-template-columns: repeat(2, 1fr); }
`;

const RowA = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1.05fr;
  gap: 10px;
  margin-bottom: 10px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const RowB = styled.div`
  display: grid;
  grid-template-columns: 1.25fr 1fr 1fr;
  gap: 10px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const SignupRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  &:first-child { border-top: none; }
`;

const Who = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`;

const Stat = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 0;
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  font-size: ${({ theme }) => theme.fontSize.sm};
  &:first-child { border-top: none; }
  color: ${({ theme }) => theme.color.textMuted};
`;

const TONES = ['brand', 'blue', 'mint', 'ember', 'magenta', 'teal'];
const KPI_ICONS = ['users', 'user-plus', 'trending-up', 'activity', 'bar-chart-3', 'verify'] as const;

const QuickActionsRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 10px;
`;

export interface DashboardSectionProps {
  rangeDays?: number;
}

export function DashboardSection({ rangeDays = 7 }: DashboardSectionProps) {
  const { data, loading, error } = useOwnerDashboard(rangeDays);
  const { logAction } = useOwnerAction();
  if (loading) return <p style={{ opacity: 0.6 }}>Loading…</p>;
  if (error || !data) return <EmptyState icon="activity" message={error ?? 'No data'} />;
  const distTotal = data.distribution.reduce((s, d) => s + d.value, 0);
  return (
    <>
      <KPIS>
        {data.kpis.map((k, i) => (
          <KpiCard
            key={k.key}
            icon={KPI_ICONS[i] ?? 'activity'}
            tone={TONES[i] ?? 'brand'}
            label={k.label}
            value={k.value}
            deltaPct={k.deltaPct}
            trend={k.trend}
          />
        ))}
      </KPIS>

      <RowA>
        <OwnerPanel title="User Growth">
          <ComboChart points={data.growth} ariaLabel="New users per day with cumulative total" />
        </OwnerPanel>
        <OwnerPanel title="Distribution">
          <Donut slices={data.distribution} total={distTotal} ariaLabel="Users by plan and status" />
        </OwnerPanel>
        <OwnerPanel title="Recent Signups">
          {data.recentSignups.length === 0 ? (
            <EmptyState icon="user-plus" message="No signups in this range" />
          ) : (
            data.recentSignups.map((s) => (
              <SignupRow key={s.id}>
                <Who>
                  <Avatar name={s.name} avatarKey={s.avatarKey} size={30} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      {s.name} {s.verified && <Icon name="verify" size={12} />}
                    </div>
                    <div style={{ opacity: 0.6, fontSize: 12 }}>{s.handle}</div>
                  </div>
                </Who>
                <span style={{ opacity: 0.6, fontSize: 12 }}>
                  {new Date(s.joinedAt).toLocaleDateString()}
                </span>
              </SignupRow>
            ))
          )}
        </OwnerPanel>
      </RowA>

      <RowB>
        <OwnerPanel title="User Statistics">
          {data.userStats.map((r) => (
            <Stat key={r.label}>
              <span>{r.label}</span>
              <b>{r.value}</b>
            </Stat>
          ))}
        </OwnerPanel>
        <OwnerPanel title="Platform Activity">
          <Stat><span>Posts Published</span><b>{data.activity.posts}</b></Stat>
          <Stat><span>Comments</span><b>{data.activity.comments}</b></Stat>
          <Stat><span>Cherishes</span><b>{data.activity.likes}</b></Stat>
          <Stat>
            <span>Reports Sub / Resolved</span>
            <b>{data.activity.reportsSubmitted} / {data.activity.reportsResolved}</b>
          </Stat>
        </OwnerPanel>
        <OwnerPanel title="System Health">
          {data.health.map((h) => (
            <Stat key={h.key}>
              <span><StatusDot status={h.status} /> {h.label}</span>
              <b>{h.status}</b>
            </Stat>
          ))}
        </OwnerPanel>
      </RowB>

      <OwnerPanel title="Quick Actions">
        <QuickActionsRow>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => logAction('announcement', 'platform')}
          >
            <Icon name="megaphone" size={15} /> Announcement
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => logAction('maintenance', 'platform')}
          >
            <Icon name="wrench" size={15} /> Maintenance
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => logAction('export-users', 'system')}
          >
            <Icon name="download" size={15} /> Export Users
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => logAction('clear-cache', 'system')}
          >
            <Icon name="refresh" size={15} /> Clear Cache
          </Button>
        </QuickActionsRow>
      </OwnerPanel>
    </>
  );
}
